export type SseMessage = {
  event: string
  data: string
  id: string | null
  retry: number | null
}

/**
 * Incremental Server-Sent Events parser (WHATWG rules): feed text chunks, get complete messages.
 * Used by the web/native client (fetch streaming, so Bearer headers work) and by tests.
 */
export class SseParser {
  private buffer = ''
  private event = ''
  private data: string[] = []
  private id: string | null = null
  private retry: number | null = null
  /** Last event id seen, persists across messages (like EventSource.lastEventId). */
  lastEventId: string | null = null

  push(chunk: string): SseMessage[] {
    this.buffer += chunk
    const messages: SseMessage[] = []
    let newline = this.buffer.search(/\r\n|\r|\n/)
    while (newline !== -1) {
      const line = this.buffer.slice(0, newline)
      const sepLength = this.buffer.startsWith('\r\n', newline) ? 2 : 1
      // A lone trailing "\r" might be the first half of "\r\n": wait for more input.
      if (this.buffer[newline] === '\r' && newline + 1 === this.buffer.length) break
      this.buffer = this.buffer.slice(newline + sepLength)
      const message = this.processLine(line)
      if (message) messages.push(message)
      newline = this.buffer.search(/\r\n|\r|\n/)
    }
    return messages
  }

  private processLine(line: string): SseMessage | null {
    if (line === '') return this.dispatch()
    if (line.startsWith(':')) return null
    const colon = line.indexOf(':')
    const field = colon === -1 ? line : line.slice(0, colon)
    let value = colon === -1 ? '' : line.slice(colon + 1)
    if (value.startsWith(' ')) value = value.slice(1)
    switch (field) {
      case 'event':
        this.event = value
        break
      case 'data':
        this.data.push(value)
        break
      case 'id':
        if (!value.includes('\0')) this.id = value
        break
      case 'retry':
        if (/^\d+$/.test(value)) this.retry = Number(value)
        break
    }
    return null
  }

  private dispatch(): SseMessage | null {
    if (this.id !== null) this.lastEventId = this.id
    const hasData = this.data.length > 0
    const message: SseMessage = {
      event: this.event || 'message',
      data: this.data.join('\n'),
      id: this.id,
      retry: this.retry,
    }
    this.event = ''
    this.data = []
    this.id = null
    this.retry = null
    return hasData ? message : null
  }
}
